import { IApiResult } from 'src/generated-types/api-result'
import { useDelete, useGet, usePost, usePut } from './reactQuery'
import { IProject, IProjectInfo, IProjectWithPermission } from 'src/generated-types/project'
import { queryClient } from 'src/query-client'
import { projectQueryKeys } from 'src/query-keys'
import { CreateAttachments } from 'src/generated-types/attachment'
import { IFileWithOwner, IPosition } from 'src/generated-types'
import { IFacilityWithTypes } from 'src/generated-types/facility'
import { IProjectStatus } from 'src/generated-types/project-status'
import { CreateProjectLog, ProjectLog } from 'src/generated-types/project-log'
import { IProjectVersion } from 'src/generated-types/project-version'
import { IProjectVersionChanges } from 'src/generated-types/project-version-changes'
import { IPublishPreview, IPublishRevision } from 'src/generated-types/project-publish'
import { IMooringSyncDecision } from 'src/generated-types/project-mooring-sync'

import { IexportFile } from 'src/generated-types'
const baseUrl = (customerId: EntityId, localityId: EntityId) =>
  `customers/${customerId}/localities/${localityId}/projects`

const baseUrlWithoutLocality = (customerId: EntityId) => `customers/${customerId}/projects`

export const projectEndpoints = {
  getProjectById(customerId: EntityId, localityId: EntityId, projectId: EntityId) {
    return `${baseUrl(customerId, localityId)}/${projectId}`
  },
  getProjects(customerId: EntityId) {
    return `${baseUrlWithoutLocality(customerId)}`
  },
  getProjectsBasedOnSearchInput(customerId: EntityId, searchInput: string) {
    return `${baseUrlWithoutLocality(customerId)}?searchInput=${searchInput}`
  },
  createProject(customerId: EntityId, localityId: EntityId) {
    return `${baseUrl(customerId, localityId)}`
  },
  updateProject(customerId: EntityId, localityId: EntityId) {
    return `${baseUrl(customerId, localityId)}`
  },
  deleteProject(customerId: EntityId, localityId: EntityId) {
    return `${baseUrl(customerId, localityId)}`
  },
  updateProjectGridLines(customerId: EntityId, localityId: EntityId, projectId: EntityId) {
    return `${baseUrl(customerId, localityId)}/${projectId}/grid-lines`
  },
  getAuthorizedProjects(customerId: EntityId, searchInput?: string) {
    return `${baseUrlWithoutLocality(customerId)}/get-authorized-projects${
      searchInput ? `?searchInput=${searchInput}` : ''
    }`
  },
  getProjectFiles(customerId: EntityId, localityId: EntityId, projectId: EntityId) {
    return `${baseUrl(customerId, localityId)}/${projectId}/files`
  },
  saveProjectFiles(customerId: EntityId, localityId: EntityId, projectId: EntityId) {
    return `${baseUrl(customerId, localityId)}/${projectId}/save-files`
  },
  exportExcelQuotation(customerId: EntityId, localityId: EntityId) {
    return `${baseUrl(customerId, localityId)}/generate-excel-quotation`
  },
  exportPDFQuotation(customerId: EntityId, localityId: EntityId) {
    return `${baseUrl(customerId, localityId)}/generate-pdf-quotation`
  },
  exportWordQuotation(customerId: EntityId, localityId: EntityId) {
    return `${baseUrl(customerId, localityId)}/generate-word-quotation`
  },
  uploadProjectOlex(customerId: EntityId, localityId: EntityId) {
    return `${baseUrl(customerId, localityId)}/upload-olex`
  },
  getProjectMapSVG(customerId: EntityId, localityId: EntityId, projectId: EntityId) {
    return `${baseUrl(customerId, localityId)}/${projectId}/get-project-map-svg`
  },
  getProjectsForDropDown(customerId: EntityId) {
    return `${baseUrlWithoutLocality(customerId)}/access-control-drop-down`
  },
  getProjectForMap(customerId: EntityId, localityId: EntityId, projectId: EntityId) {
    return `${baseUrl(customerId, localityId)}/${projectId}/project-for-map`
  },
  generatePositionsForProject(customerId: EntityId, localityId: EntityId, projectId: EntityId) {
    return `${baseUrl(customerId, localityId)}/${projectId}/generate-positions`
  },
  publishProject(customerId: EntityId, localityId: EntityId, projectId: EntityId) {
    return `${baseUrl(customerId, localityId)}/${projectId}/publish`
  },
  projectVersions(customerId: EntityId, localityId: EntityId, projectId: EntityId) {
    return `${baseUrl(customerId, localityId)}/${projectId}/versions`
  },
  discardProjectVersion(customerId: EntityId, localityId: EntityId) {
    return `${baseUrl(customerId, localityId)}/versions`
  },
  projectVersionPositions(customerId: EntityId, localityId: EntityId, projectId: EntityId) {
    return `${baseUrl(customerId, localityId)}/${projectId}/versions/positions`
  },
  getProjectLogs(customerId: EntityId, localityId: EntityId, projectId: EntityId) {
    return `${baseUrl(customerId, localityId)}/${projectId}/logs`
  },
  addProjectLog(customerId: EntityId, localityId: EntityId, projectId: EntityId) {
    return `${baseUrl(customerId, localityId)}/${projectId}/logs/create`
  },
  deleteProjectLog(customerId: EntityId, localityId: EntityId, projectId: EntityId) {
    return `${baseUrl(customerId, localityId)}/${projectId}/delete-log`
  },
  editProjectLog(customerId: EntityId, localityId: EntityId, projectId: EntityId, logId: EntityId) {
    return `${baseUrl(customerId, localityId)}/${projectId}/update-log/${logId}`
  },
}

export const invalidateProjectCache = {
  getProjects(customerId: EntityId) {
    queryClient.invalidateQueries(projectQueryKeys.GET_PROJECTS(customerId))
  },
  getProject(customerId: EntityId, localityId: EntityId, projectId: EntityId) {
    queryClient.invalidateQueries(projectQueryKeys.GET_PROJECT(customerId, localityId, projectId))
  },
  getAuthorizedProjects(customerId: EntityId, searchInput?: string) {
    queryClient.invalidateQueries(projectEndpoints.getAuthorizedProjects(customerId, searchInput))
  },
  getProjectFiles(customerId: EntityId, localityId: EntityId, projectId: EntityId) {
    queryClient.invalidateQueries(projectEndpoints.getProjectFiles(customerId, localityId, projectId))
  },
  getProjectVersions(customerId: EntityId, localityId: EntityId, projectId: EntityId) {
    queryClient.invalidateQueries(projectQueryKeys.GET_PROJECT_VERSIONS(customerId, localityId, projectId))
  },
  getProjectMapSVG(customerId: EntityId, localityId: EntityId, projectId: EntityId) {
    queryClient.invalidateQueries(projectEndpoints.getProjectMapSVG(customerId, localityId, projectId))
  },
  getProjectForMap(customerId: EntityId, localityId: EntityId, projectId: EntityId) {
    queryClient.invalidateQueries(projectEndpoints.getProjectForMap(customerId, localityId, projectId))
  },
  getProjectLogs(customerId: EntityId, localityId: EntityId, projectId: EntityId) {
    queryClient.invalidateQueries(projectEndpoints.getProjectLogs(customerId, localityId, projectId))
  },
  getProjectExportFiles(customerId: EntityId, localityId: EntityId, projectId: EntityId) {
    queryClient.invalidateQueries(projectExportFilesEndpoints.getProjectExportFiles(customerId, localityId, projectId))
  },
}

export const useGetProjects = (customerId: EntityId, searchInput?: string) =>
  useGet<IProjectInfo[]>(
    searchInput
      ? projectEndpoints.getProjectsBasedOnSearchInput(customerId, searchInput)
      : projectEndpoints.getProjects(customerId),
  )

export const useGetProject = (customerId: EntityId, localityId: EntityId, projectId: EntityId) =>
  useGet<IProjectWithPermission>(
    projectEndpoints.getProjectById(customerId, localityId, projectId),
    {},
    { enabled: projectId != null, queryKey: projectQueryKeys.GET_PROJECT(customerId, localityId, projectId) },
  )

export const useCreateProject = (customerId: EntityId, localityId: EntityId) =>
  usePost<IApiResult, IProject>(projectEndpoints.createProject(customerId, localityId))

export const useUpdateProject = (customerId: EntityId, localityId: EntityId) =>
  usePut<IApiResult, IProject>(projectEndpoints.updateProject(customerId, localityId))

export const useDeleteProject = (customerId: EntityId, localityId: EntityId) =>
  useDelete<IProject>(projectEndpoints.deleteProject(customerId, localityId))

export const useUpdateProjectGridLines = (customerId: EntityId, localityId: EntityId, projectId: EntityId) =>
  usePut<IApiResult, { gridLineWidthInMeters?: number | null; gridLineHeightInMeters?: number | null }>(
    projectEndpoints.updateProjectGridLines(customerId, localityId, projectId),
  )

export const useGetAuthorizedProjects = (
  customerId: EntityId,
  searchInput?: string,
  isSidebar?: boolean,
  localityIds?: EntityId[],
  userIds?: EntityId[],
  startDate?: string,
  endDate?: string,
  status?: IProjectStatus[],
  referenceIds?: EntityId[],
) =>
  useGet<IProjectInfo[]>(projectEndpoints.getAuthorizedProjects(customerId, searchInput), {
    localityIds,
    userIds,
    startDate,
    endDate,
    status,
    referenceIds,
    isSidebar,
  })

export const projectExportFilesEndpoints = {
  getProjectExportFiles(customerId: EntityId, localityId: EntityId, projectId: EntityId) {
    return `${baseUrl(customerId, localityId)}/${projectId}/exportFiles`
  },
}

export const useGetProjectFiles = (customerId: EntityId, localityId: EntityId, projectId: EntityId) =>
  useGet<IFileWithOwner[]>(projectEndpoints.getProjectFiles(customerId, localityId, projectId))

export const useSaveProjectFiles = (customerId: EntityId, localityId: EntityId, projectId: EntityId) =>
  usePost<IApiResult, CreateAttachments>(projectEndpoints.saveProjectFiles(customerId, localityId, projectId), {
    invalidateQueries: [projectQueryKeys.GET_PROJECT(customerId, localityId, projectId)],
  })

export const useExportExcelQuotation = (customerId: EntityId, localityId: EntityId) =>
  usePost<IProject, object>(
    projectEndpoints.exportExcelQuotation(customerId, localityId),
    {},
    { responseType: 'arraybuffer' },
  )

export const useExportPDFQuotation = (customerId: EntityId, localityId: EntityId) =>
  usePost<IProject, object>(
    projectEndpoints.exportPDFQuotation(customerId, localityId),
    {},
    { responseType: 'arraybuffer' },
  )

export const useExportWordQuotation = (customerId: EntityId, localityId: EntityId) =>
  usePost<IProject, object>(
    projectEndpoints.exportWordQuotation(customerId, localityId),
    {},
    { responseType: 'arraybuffer' },
  )

export const useUploadProjectOlex = (customerId: EntityId, localityId: EntityId) =>
  usePost<IPosition[], FormData>(projectEndpoints.uploadProjectOlex(customerId, localityId))

export const useGetProjectMapSVG = (customerId: EntityId, localityId: EntityId, projectId: EntityId, enabled) =>
  useGet<string>(projectEndpoints.getProjectMapSVG(customerId, localityId, projectId), { enabled })

export const useGetProjectsForDropDown = ({ clientId }: { clientId: EntityId }, config?: { enabled?: boolean }) =>
  useGet<IFacilityWithTypes[]>(projectEndpoints.getProjectsForDropDown(clientId), undefined, config)

export const useGetProjectForMap = (customerId: EntityId, localityId: EntityId, projectId: EntityId) =>
  useGet<IProjectInfo>(projectEndpoints.getProjectForMap(customerId, localityId, projectId))

export const useGeneratePositionsForProject = (customerId: EntityId, localityId: EntityId, projectId: EntityId) =>
  usePost<IPosition[], FormData>(projectEndpoints.generatePositionsForProject(customerId, localityId, projectId))

export const usePublishProject = (customerId: EntityId, localityId: EntityId, projectId: EntityId) =>
  usePut<IProjectInfo, object>(projectEndpoints.publishProject(customerId, localityId, projectId))

export const useGetProjectVersions = (
  customerId: EntityId,
  localityId: EntityId,
  projectId: EntityId,
  enabled: boolean,
) =>
  useGet<IProjectVersion[]>(projectEndpoints.projectVersions(customerId, localityId, projectId), undefined, {
    queryKey: projectQueryKeys.GET_PROJECT_VERSIONS(customerId, localityId, projectId),
    enabled,
  })

export const useGetProjectVersionChanges = (
  customerId: EntityId,
  localityId: EntityId,
  projectId: EntityId,
  enabled: boolean,
) =>
  useGet<IProjectVersionChanges>(
    `${projectEndpoints.projectVersions(customerId, localityId, projectId)}/changes`,
    undefined,
    { enabled },
  )

export const useCreateProjectVersion = (customerId: EntityId, localityId: EntityId, projectId: EntityId) =>
  usePost<
    { projectId: number },
    { name: string; reason?: string; syncFingerprint?: string; syncDecisions?: IMooringSyncDecision[] }
  >(projectEndpoints.projectVersions(customerId, localityId, projectId))

export const useDiscardProjectVersion = (customerId: EntityId, localityId: EntityId) =>
  useDelete<{ projectId: number }>(projectEndpoints.discardProjectVersion(customerId, localityId))

export const useGetPublishPreview = (customerId: EntityId, localityId: EntityId, projectId: EntityId) =>
  useGet<IPublishPreview>(`${projectEndpoints.projectVersions(customerId, localityId, projectId)}/publish-preview`)

export const usePublishRevision = (customerId: EntityId, localityId: EntityId, projectId: EntityId) =>
  usePost<{ status: string; mooringId?: number }, IPublishRevision>(
    `${projectEndpoints.projectVersions(customerId, localityId, projectId)}/publish`,
  )

export const useGetProjectLogs = (customerId: EntityId, localityId: EntityId, projectId: EntityId, enabled: boolean) =>
  useGet<ProjectLog[]>(projectEndpoints.getProjectLogs(customerId, localityId, projectId), undefined, {
    queryKey: projectEndpoints.getProjectLogs(customerId, localityId, projectId),
    enabled,
  })

export const useAddProjectLog = (customerId: EntityId, localityId: EntityId, projectId: EntityId) =>
  usePost<ProjectLog, CreateProjectLog>(projectEndpoints.addProjectLog(customerId, localityId, projectId))

export const useDeleteProjectLog = (customerId: EntityId, localityId: EntityId, projectId: EntityId) =>
  useDelete(projectEndpoints.deleteProjectLog(customerId, localityId, projectId))

export const useGetProjectExportFiles = (customerId: EntityId, localityId: EntityId, projectId: EntityId) =>
  useGet<IexportFile[] | null>(
    projectExportFilesEndpoints.getProjectExportFiles(customerId, localityId, projectId),
    undefined,
    {
      queryKey: projectExportFilesEndpoints.getProjectExportFiles(customerId, localityId, projectId),
    },
  )

export const useEditProjectLog = (customerId: EntityId, localityId: EntityId, projectId: EntityId, logId: EntityId) =>
  usePut<IApiResult, { message: string; fileIds?: number[] }>(
    projectEndpoints.editProjectLog(customerId, localityId, projectId, logId),
  )
